import { useCallback, useEffect, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { datadogLogs } from '@datadog/browser-logs';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { TextBlock } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { postHexEmbedUrl } from '../../../Api/Analytics';
import { getSelectedAccountIdsForUrl, getSelectedAccountsSubject } from '../../../store/User';

export const HexEmbed = () => {
  const [embedUrl, setEmbedUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let initialized = false;
    let currentKey = '';
    let latestRequestId = 0;

    const loadEmbed = async () => {
      const requestId = ++latestRequestId;
      setLoading(true);
      setFailed(false);
      const { data, error } = await postHexEmbedUrl(getSelectedAccountIdsForUrl());
      // Only the most recently started request may update state, so a slow earlier request
      // can't overwrite a newer one — even for the same account (an A -> B -> A sequence).
      if (!active || requestId !== latestRequestId) return;
      if (data?.url) {
        setEmbedUrl(data.url);
      } else {
        setFailed(true);
        datadogLogs.logger.error('Hex Embed Error', { error });
      }
      setLoading(false);
    };

    // Re-scope the embed to follow the Portal account switcher (like the ThoughtSpot
    // liveboard). A fresh single-use URL is minted per selection; scope is still
    // enforced server-side, so the selection can only ever narrow.
    const subscription = getSelectedAccountsSubject().subscribe((accounts) => {
      const key = (accounts || [])
        .map((a) => a.id)
        .sort()
        .join(',');
      if (initialized && key === currentKey) return;
      initialized = true;
      currentKey = key;
      loadEmbed();
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  // onLoad fires even if Hex renders its own error page (expired URL, domain not
  // allow-listed); cross-origin means we cannot read the child, so log a neutral
  // event, not a success.
  const handleLoad = useCallback(() => datadogLogs.logger.info('Hex embed iframe load event'), []);

  if (loading) return <ComponentSpinner />;

  if (failed) {
    return (
      <Box
        role="alert"
        sx={{ px: { xs: 2, sm: 6 }, py: 4, textAlign: 'center' }}>
        <Paper sx={{ p: 4 }}>
          <div className="flex items-center gap-2 mb-2 justify-center">
            <Icon type="alert" />
            <TextBlock
              size="lg"
              weight="bold"
              color="orange">
              Analytics Unavailable
            </TextBlock>
          </div>
          <TextBlock>We could not load your analytics. Please contact support.</TextBlock>
        </Paper>
      </Box>
    );
  }

  return (
    // No sandbox: Hex needs scripts, forms, downloads, and clipboard, and it renders
    // first-party, server-scoped content over a single-use signed URL.
    <iframe
      title="Hex Analytics"
      src={embedUrl}
      allow="clipboard-write"
      className="flex-1 min-h-0 w-full"
      style={{ border: 'none' }}
      onLoad={handleLoad}
    />
  );
};
